import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enPages from './translations/en';
import esPages from './translations/es';

const LanguageContext = createContext();

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

const translations = {
    fr: {
        common: {
            loading: "Chargement...",
            error: "Erreur",
            save: "Sauvegarder",
            cancel: "Annuler",
            edit: "Modifier",
            delete: "Supprimer",
            add: "Ajouter",
            back: "Retour",
            confirm: "Confirmer",
            yes: "Oui",
            no: "Non",
            search: "Rechercher",
            filter: "Filtrer",
            settings: "Paramètres",
            logout: "Déconnexion",
            welcome: "Bienvenue",
            name: "Nom",
            description: "Description",
            date: "Date",
            time: "Heure",
            status: "Statut",
            actions: "Actions",
            submit: "Soumettre",
            reset: "Réinitialiser",
            close: "Fermer",
            open: "Ouvrir",
            select: "Sélectionner",
            none: "Aucun",
            all: "Tous",
            from: "De",
            to: "À",
            at: "à",
            refresh: "Actualiser",
            unknown: "Inconnu",
            noResults: "Aucun résultat",
            clearFilters: "Effacer les filtres",
            irreversible: "Cette action est irréversible"
        },
        server: {
            title: "Configuration du Serveur",
            subtitle: "Association des salons Discord avec les types d'attaque et de défense",
            serverName: "Nom du serveur",
            serverIcon: "Icône du serveur",
            settings: "Paramètres généraux",
            permissions: "Permissions",
            roles: "Rôles",
            channels: "Salons",
            generalConfig: "Configuration générale",
            configName: "Nom de la configuration",
            configNamePlaceholder: "Ex: Configuration GDC Été 2024",
            specialRole: "Rôle des gestionnaires",
            noSpecialRole: "Aucun rôle spécial",
            channelsAssociation: "Association des Salons",
            attackConfig: "Configuration Attaque",
            defenseConfig: "Configuration Défense",
            tempoConfig: "Configuration Tempo",
            against: "Contre",
            selectChannel: "Sélectionner un salon",
            instructions: "Instructions",
            attackInstruction: "Sélectionnez les salons où les membres posteront leurs attaques selon le nombre d'ennemis défaits.",
            defenseInstruction: "Sélectionnez les salons où les membres posteront leurs défenses selon le nombre d'ennemis repoussés.",
            tempoInstruction: "Sélectionnez le salon où les membres posteront leurs combats défensifs pour les points tempo.",
            specialRoleInstruction: "Optionnel - Rôle qui aura des permissions spéciales dans le système.",
            discordChannels: "Salons Discord",
            configuredChannels: "Salons Configurés",
            availableRoles: "Rôles Disponibles",
            configuration: "Configuration",
            progression: "Progression",
            reset: "Réinitialiser",
            save: "Sauvegarder la configuration",
            saving: "Sauvegarde en cours...",
            unsavedChanges: "Vous avez des modifications non sauvegardées",
            loadingConfig: "Chargement de la configuration...",
            configNameRequired: "Veuillez entrer un nom de configuration !",
            configSaved: "Configuration sauvegardée avec succès !",
            configSaveFailed: "Échec de la sauvegarde !",
            success: "Configuration sauvegardée avec succès !",
            error: "Erreur lors de la sauvegarde de la configuration",
            confirmReset: "Voulez-vous vraiment réinitialiser la configuration ?"
        },
        seasonsPage: {
            title: "Saisons",
            subtitle: "Créez et gérez vos saisons de GDC",
            create: "Créer une saison",
            merge: "Fusion des classements",
            filterAll: "Toutes",
            filterActive: "En cours",
            filterUpcoming: "À venir",
            filterFinished: "Terminées",
            sortLabel: "Trier",
            sortNewest: "Plus récentes",
            sortOldest: "Plus anciennes",
            sortEnd: "Date de fin",
            sortTitle: "Titre",
            sortModified: "Modifié par",
            colSeason: "Saison",
            colStatus: "Statut",
            colPeriod: "Période",
            colModifiedBy: "Modifié par",
            colMemberStats: "Stats membres",
            autoPublish: "Publication auto",
            open: "Ouvrir",
            archive: "Archiver",
            unarchive: "Désarchiver",
            archivedSection: "Saisons archivées ({n})",
            archivedHint: "Les saisons archivées restent intactes : elles sont simplement rangées ici.",
            archived: "Saison archivée",
            unarchived: "Saison désarchivée",
            archiveError: "Impossible de modifier l'archivage",
            hideFromStats: "Masquer des stats membres",
            showInStats: "Afficher dans les stats membres"
        },
        adminHome: {
            collapse: "Réduire le menu",
            expand: "Agrandir le menu",
            openMenu: "Ouvrir le menu",
            closeMenu: "Fermer le menu",
            backToServers: "Mes serveurs",
            adminArea: "Administration",
            currentSeason: "Saison en cours",
            noSeason: "Aucune saison en cours",
            noSeasonHint: "Lance une saison pour démarrer un nouveau classement.",
            createSeason: "Créer une saison",
            viewSeason: "Ouvrir la saison",
            daysLeft: "{n} jours restants",
            lastDay: "Dernier jour",
            rankedPlayers: "{n} joueurs classés",
            top3: "Top 3",
            pts: "pts",
            unknownPlayer: "Joueur inconnu",
            noRanking: "Pas encore de points cette saison.",
            statsTitle: "Le serveur en chiffres",
            pepites: "Pépites récoltées",
            pepitesCurrent: "Pépites en cours",
            pepitesCurrentDetail: "Depuis la dernière distribution",
            pepitesTotalDetail: "Au total, distributions comprises",
            statsError: "Impossible de charger les chiffres du serveur.",
            pagesTitle: "Gérer le serveur"
        },
        navigation: {
            dashboard: "Tableau de bord",
            seasons: "Saisons",
            events: "Événements",
            players: "Joueurs",
            configuration: "Configuration",
            bareme: "Barème",
            tempo: "Tempo",
            reservation: "Réservation",
            statistics: "Statistiques",
            pepites: "Pépites",
            language: "Langue",
            backToServerList: "Retour à la liste des serveurs",
            backToServerListDescription: "Revenir à la sélection de serveur"
        },
        login: {
            title: "Connexion Discord",
            subtitle: "Connectez-vous avec votre compte Discord",
            button: "Se connecter avec Discord",
            processing: "Connexion en cours...",
            error: "Erreur de connexion"
        },
        welcome: {
            title: "Bienvenue !",
            subtitle: "Sélectionnez un serveur pour commencer",
            noServers: "Aucun serveur disponible",
            selectServer: "Sélectionner le serveur",
            adminAccess: "Administrateur",
            memberAccess: "Espace Membre",
            hello: "Bonjour, {name}",
            searchPlaceholder: "Rechercher un serveur",
            noMatch: "Aucun serveur ne correspond à « {term} »",
            noAccess: "Le bot n'est sur aucun de tes serveurs Discord pour le moment.",
            roleAdmin: "Admin",
            roleMember: "Membre",
            openAdmin: "Administrer",
            favoriteAdd: "Ajouter aux favoris",
            favoriteRemove: "Retirer des favoris"
        },
        member: {
            badge: "Espace Membre",
            hello: "Bonjour",
            welcomeText: "Bienvenue dans votre espace personnel.",
            preferencesTitle: "Mes préférences de zones",
            preferencesDesc: "Classez les zones Dofus par ordre de préférence pour les sessions de réservation.",
            statsTitle: "Mes statistiques",
            statsDesc: "Vos performances lors des GDC : attaques, défenses, points obtenus.",
            reservationTitle: "Mes réservations",
            reservationDesc: "Consultez votre zone réservée pour la session en cours.",
            comingSoon: "Bientôt disponible"
        },        dashboard: {
            title: "Tableau de bord",
            serverConfig: "Configuration du serveur",
            seasonManagement: "Gestion des saisons",
            eventManagement: "Gestion des événements",
            reservationManagement: "Gestion des réservations",
            statisticsManagement: "Statistiques des joueurs",
            pepitesManagement: "Gestion des pépites",
            scoreSettings: "Paramètres de score",
            tempoManagement: "Gestion des points tempo",
            // Nouveau contenu pour AccueilPage
            welcomeTitle: "Tableau de bord de {serverName}",
            welcomeSubtitle: "Gérez votre serveur Discord et vos événements GDC",
            language: "Langue",
            statistics: "Statistiques",
            quickStats: {
                configuredChannels: "Salons configurés",
                baremes: "Barèmes",
                seasons: "Saisons",
                events: "Événements"
            },
            statsCards: {
                server: {
                    title: "Serveur",
                    totalChannels: "Salons totaux:",
                    configuredChannels: "Salons configurés:",
                    availableRoles: "Rôles disponibles:"
                },
                baremes: {
                    title: "Barèmes",
                    created: "Barèmes créés",
                    manage: "Gérer les barèmes"
                },
                seasons: {
                    title: "Saisons",
                    created: "Saisons créées",
                    manage: "Gérer les saisons"
                },
                events: {
                    title: "Événements",
                    planned: "Événements planifiés",
                    manage: "Gérer les événements"
                }
            },
            quickActions: {
                title: "Actions Rapides",
                configureChannels: {
                    title: "Configurer les Salons",
                    description: "Associez vos salons Discord aux types d'attaque/défense"
                },
                createBareme: {
                    title: "Créer un Barème",
                    description: "Définissez les points d'attaque et de défense"
                },
                launchSeason: {
                    title: "Lancer une Saison",
                    description: "Créez une nouvelle saison de GDC avec classements"
                },
                planEvent: {
                    title: "Planifier un Événement",
                    description: "Organisez vos guerres de clans et tournois"
                }
            },
            serverStatus: "En ligne"
        },
        seasons: {
            title: "Gestion des Saisons",
            createNew: "Créer une nouvelle saison",
            noSeasons: "Aucune saison trouvée",
            currentSeason: "Saison actuelle",
            startDate: "Date de début",
            endDate: "Date de fin",
            active: "Active",
            inactive: "Inactive",
            upcoming: "À venir",
            finished: "Terminée",
            archived: "Archivée",
            seasonsPlural: "saisons",
            seasonSingular: "saison",
            allSeasons: "Toutes",
            loading: "Chargement des saisons",
            createFirstSeason: "Créer la première saison",
            noResultsMessage: "Aucune saison ne correspond à vos critères de recherche",
            autoPublishEnabled: "Publication automatique activée",
            modifiedBy: "Modifié par",
            edit: "Modifier la saison",
            delete: "Supprimer la saison",
            confirmDelete: "Êtes-vous sûr de vouloir supprimer cette saison ?",
            deleteSuccess: "Saison supprimée avec succès",
            deleteError: "Erreur lors de la suppression de la saison"
        },        createSeason: {
            title: "Créer une nouvelle saison",
            progress: "Progression",
            name: "Nom de la saison",
            namePlaceholder: "Ex: Saison Été 2024",
            description: "Description",
            descriptionPlaceholder: "Description de la saison...",
            startDate: "Date de début",
            endDate: "Date de fin",
            create: "Créer la saison",
            creating: "Création en cours...",
            success: "Saison créée avec succès !",
            error: "Erreur lors de la création de la saison",
            configurationSection: "Configuration des Barèmes",
            attackBareme: "Barème Attaque",
            defenseBareme: "Barème Défense",
            selectAttackBareme: "Sélectionner un barème d'attaque",
            selectDefenseBareme: "Sélectionner un barème de défense",
            noAttackBareme: "Aucun barème d'attaque disponible",
            noDefenseBareme: "Aucun barème de défense disponible",
            publicationChannel: "Canal de Publication",
            selectChannel: "Sélectionner un canal",
            seasonPeriod: "Période de la Saison",
            descriptionAndOptions: "Description et Options",
            descriptionPlaceholderLong: "Décrivez les règles et objectifs de cette saison...",
            autoPublish: "Publication automatique du classement en fin de saison",
            requiredFields: "Champs obligatoires",
            createdBy: "Créé par",
            cancel: "Annuler",
            createSeason: "Créer la Saison",            validation: {
                nameRequired: "Le nom est obligatoire",
                startDateRequired: "La date de début est requise",
                endDateRequired: "La date de fin est requise",
                publicationChannelRequired: "Le canal de publication est requis",
                attackBaremeRequired: "Le barème d'attaque est requis",
                defenseBaremeRequired: "Le barème de défense est requis",
                descriptionRequired: "La description est requise",
                endDateAfterStart: "La date de fin doit être après la date de début"
            }
        },editSeason: {
            title: "Modifier la saison",
            updating: "Mise à jour en cours...",
            success: "Saison mise à jour avec succès !",
            error: "Erreur lors de la mise à jour de la saison",
            notFound: "Saison non trouvée",
            configuration: "Configuration",
            ranking: "Classement",
            seasonName: "Nom de la Saison",
            startDate: "Date de début",
            endDate: "Date de fin",
            attackBareme: "Barème Attaque",
            defenseBareme: "Barème Défense",
            selectBareme: "Sélectionner un barème",
            autoPublish: "Publication automatique du classement en fin de saison",
            save: "Sauvegarder",
            refresh: "Actualiser",
            publish: "Publier",
            cancel: "Annuler",
            loadingError: "Erreur chargement saison",
            seasonNotFound: "Saison non trouvée !",
            channelsError: "Erreur récupération salons",
            baremesError: "Erreur récupération barèmes",
            rankingError: "Erreur classement",
            saveModifications: "Sauver modifs",
            usernames: "Pseudos",
            players: "joueurs",
            modifications: "modifs",
            loadingUsernames: "Chargement des pseudos",
            player: "Joueur",
            attackDefense: "Att/Def",
            attack: "Attaque",
            defense: "Défense",
            tempo: "Tempo",
            points: "Points",actions: "Actions",
            total: "Total",
            events: "Événements",
            rank: "Rang",
            saveModificationsSuccess: "Modifications enregistrées !",
            saveModificationsFailed: "Échec de l'enregistrement !",
            recalculateConfirm: "Voulez-vous recalculer les points de cette saison en tenant compte des événements actifs ?",
            recalculateSuccess: "Saison recalculée avec succès avec prise en compte des événements !",
            recalculateError: "Erreur lors du recalcul de la saison avec événements !",
            seasonAndModificationsSaved: "Saison et modifications enregistrées !",
            updateError: "Erreur lors de la mise à jour !",
            playerHistoryError: "Impossible de charger l'historique du joueur.",
            playerNotFoundWarning: "Joueur non trouvé dans la DB, fallback local.",            refreshUsernamesSuccess: "pseudos mis à jour avec succès",
            refreshUsernamesPartial: "Rafraîchissement terminé: {success} noms mis à jour, {error} échecs. Réessayez si nécessaire.",
            refreshUsernamesError: "Erreur lors du rafraîchissement des noms d'utilisateur !",
            refreshUsernames: "Pseudos",
            refreshUsernamesTooltip: "Rafraîchir tous les pseudos depuis Discord",
            backToSeasons: "Retour aux saisons",
            dashboard: "Tableau de bord",
            playerModifications: "Modifications de",
            playerIdShort: "Joueur",
            noModifications: "Aucune modification enregistrée.",
            by: "par",
            on: "le",
            pts: "pts",
            publishRanking: "Publication du classement",
            selectChannel: "Veuillez sélectionner un canal de publication :",
            channelsAvailable: "canal(x) disponible(s)",
            noChannelsAvailable: "Aucun canal disponible",
            serverId: "Serveur ID",
            publicationChannel: "Canal de publication :",
            noRankingData: "Aucune donnée de classement à publier !",
            publishButton: "Publier",
            noChannelButton: "Aucun canal",
            selectChannelButton: "Sélectionner un canal",
            noChannelSelected: "Aucun salon sélectionné",
            refreshDataError: "Erreur lors de l'actualisation des données !"
        },        events: {
            title: "Gestion des Événements",
            subtitle: "Créez et gérez les événements spéciaux de votre serveur",
            loading: "Chargement des événements...",
            loadingError: "Erreur de chargement",
            retry: "Réessayer",
            addEvent: "Ajouter un événement",
            noEvents: "Aucun événement configuré",
            eventName: "Nom de l'événement",
            eventType: "Type d'événement",
            salon: "Salon",
            addSalon: "Ajouter un salon",
            removeSalon: "Supprimer le salon",
            save: "Sauvegarder les événements",
            saving: "Sauvegarde en cours...",
            success: "Événements sauvegardés avec succès !",
            error: "Erreur lors de la sauvegarde des événements",
            validation: {
                nameRequired: "Le nom de l'événement est obligatoire",
                typeRequired: "Le type d'événement est obligatoire",
                salonRequired: "Au moins un salon est requis"
            },            // Configuration des événements
            tabs: {
                list: "📋 Liste des événements ({count})",
                create: "➕ Créer un événement",
                edit: "✏️ Modifier"
            },            emptyState: {
                title: "📭 Aucun événement créé",
                description: "Créez votre premier événement pour commencer !",
                createButton: "➕ Créer un événement"
            },            table: {
                status: "Statut",
                event: "Événement",
                type: "Type",
                period: "Période",
                config: "Configuration",
                actions: "Actions",
                types: "type(s)"
            },            status: {
                active: "🟢 Actif",
                scheduled: "🟡 Programmé",
                inactive: "🔴 Inactif",
                activated: "activé",
                deactivated: "désactivé"
            },            form: {
                createTitle: "➕ Créer un nouvel événement",
                editTitle: "✏️ Modifier l'événement",
                name: "Nom de l'événement",
                namePlaceholder: "Ex: Double Points Week-end",
                type: "Type d'événement",
                description: "Description",
                descriptionPlaceholder: "Description de l'événement...",
                startDate: "Date de début",
                endDate: "Date de fin",
                isActive: "Événement actif"
            },
            types: {
                pointsMultiplier: {
                    name: "Multiplicateur de Points",
                    description: "Double ou triple les points pendant une période"
                },
                specialLadder: {
                    name: "Ladder Spécial",
                    description: "Classement spécial avec des règles particulières"
                },
                combatRestriction: {
                    name: "Restriction de Combat",
                    description: "Événement limité à certains types de combat"                },
                unknownType: "Type inconnu"
            },            config: {
                multiplierTitle: "⚡ Configuration Multiplicateur",
                ladderTitle: "🏆 Configuration Ladder Spécial",
                restrictionTitle: "⚔️ Configuration Restriction Combat",
                multiplier: "Multiplicateur",
                applyToAttack: "Appliquer aux points d'attaque",
                applyToDefense: "Appliquer aux points de défense",
                allowedCombatTypes: "Types de combat autorisés",
                excludeMultipleParticipation: "Exclure les participations multiples dans le même combat",
                minParticipants: "Participants minimum",
                maxParticipants: "Participants maximum"
            },            actions: {
                edit: "Modifier l'événement",
                activate: "Activer",
                deactivate: "Désactiver",
                delete: "Supprimer l'événement",
                cancel: "❌ Annuler",
                create: "➕ Créer",
                update: "💾 Mettre à jour",
                saving: "⏳ Sauvegarde...",
                retry: "🔄 Réessayer"
            },            messages: {
                nameRequired: "Le nom de l'événement est obligatoire !",
                datesRequired: "Les dates de début et fin sont obligatoires !",
                createSuccess: "✅ Événement créé avec succès !",
                updateSuccess: "✅ Événement mis à jour avec succès !",
                deleteSuccess: "✅ Événement supprimé avec succès !",
                toggleSuccess: "✅ Événement {status} !",
                saveError: "❌ Erreur lors de la sauvegarde !",
                deleteError: "❌ Erreur lors de la suppression !",
                toggleError: "❌ Erreur lors du changement de statut !",
                loadingError: "Erreur de chargement",
                loadingErrorTitle: "❌ Erreur de chargement",
                confirmDelete: "⚠️ Êtes-vous sûr de vouloir supprimer cet événement ?"
            },            dates: {
                from: "Du",
                to: "Au"
            },
            multipliers: {
                double: "x2 (Double)",
                triple: "x3 (Triple)"
            }
        },
        reservation: {
            subtitle: "Gestion des réservations de votre serveur",
            configTitle: "Configuration du bot",
            configSubtitle: "Définissez le salon Discord où la commande /pos sera active et la saison concernée.",
            channelLabel: "Salon de réservation",
            channelHint: "Les joueurs utiliseront /pos dans ce salon",
            selectChannel: "-- Sélectionner un salon --",
            activeSaisonLabel: "Saison active",
            activeSaisonHint: "Le classement de cette saison est utilisé pour /pos",
            selectActiveSaison: "-- Sélectionner une saison --",
            tableTitle: "Réservations",
            addRow: "Ajouter une ligne",
            colSaison: "Saison",
            colJoueur: "Joueur",
            colZone: "Zone",
            selectSaison: "-- Choisir une saison --",
            selectJoueur: "-- Choisir un joueur --",
            selectZone: "-- Zone --",
            selectSaisonFirst: "Sélectionne d'abord une saison",
            noPlayers: "Aucun joueur dans cette saison",
            autoAssign: "Auto-assigner les zones",
            noPreference: "Aucune préférence",
            sessionTitle: "Fiche de réservation",
            sessionSubtitle: "Sélectionnez ou créez une fiche pour organiser les réservations.",
            selectSessionLabel: "Fiche active",
            selectSession: "-- Sélectionner une fiche --",
            newSession: "Nouvelle fiche",
            newSessionPlaceholder: "Ex: GDC Semaine 1",
            createSession: "Créer la fiche",
            saveRows: "Sauvegarder la fiche",
            comingSoon: "Fonctionnalité à venir",
            comingSoonDescription: "La gestion des réservations sera disponible prochainement. Restez connecté !"
        },
        adminStats: {
            title: "Statistiques des joueurs",
            subtitle: "Attaques et défenses uniques par joueur",
            season: "Saison",
            allSeasons: "Toutes les saisons",
            searchPlaceholder: "Rechercher un joueur...",
            colName: "Nom",
            colAttacks: "Attaques uniques",
            colDefenses: "Défenses uniques",
            noPlayers: "Aucun joueur trouvé",
            playersCount: "joueur(s) affiché(s)",
            showZeroLabel: "Joueurs inactifs",
            showZeroPlayers: "Afficher les joueurs à 0 point"
        },
        bareme: {
            title: "Configuration du Barème",
            subtitle: "Configuration des points d'attaque et de défense",
            existingBaremes: "Barèmes Existants",
            newBareme: "Nouveau",
            createBareme: "Créer un nouveau barème",
            editBareme: "Modifier le barème",
            baremeName: "Nom du barème",
            baremeNamePlaceholder: "Ex: Barème GDC Été 2024",
            type: "Type",
            attack: "Attaque",
            defense: "Défense",
            search: "Rechercher...",
            allTypes: "Tous types",
            noBaremes: "Aucun barème créé",
            noResults: "Aucun résultat",
            completion: "Complété",
            progression: "Progression",
            totalBaremes: "Barèmes Total",
            unsavedChanges: "Modifications non sauvegardées",
            reset: "Annuler",
            save: "Sauvegarder",
            create: "Créer",
            saving: "Sauvegarde en cours...",
            delete: "Supprimer",
            enemy: "ennemi",
            enemies: "ennemis",
            allies: "alliées",
            ally: "allié",
            loadingBaremes: "Chargement des barèmes...",
            nameRequired: "Veuillez renseigner un nom pour le barème !",
            baremeUpdated: "Barème mis à jour avec succès !",
            baremeCreated: "Barème créé avec succès !",
            baremeDeleted: "Barème supprimé avec succès !",
            errorSaving: "Erreur lors de l'enregistrement !",
            errorDeleting: "Erreur lors de la suppression !",
            confirmDelete: "Voulez-vous vraiment supprimer le barème \"{name}\" ?\n\nCette action est irréversible !",
            confirmUnsaved: "Vous avez des modifications non sauvegardées. Les abandonner ?",
            addRule: "Ajouter une règle",
            noRules: "Aucune règle configurée",
            rank: "Rang",
            points: "Points",
            description: "Description",            success: "Barème sauvegardé avec succès !",
            error: "Erreur lors de la sauvegarde du barème"
        },
        tempo: {
            title: "Configuration Points Tempo",
            subtitle: "Récompensez les combats défensifs qui font perdre du temps à l'adversaire",
            existingConfigs: "Configurations Existantes",
            newConfig: "Nouvelle",
            createConfig: "Créer une nouvelle configuration",
            editConfig: "Modifier la configuration",
            configName: "Nom de la configuration",
            configNamePlaceholder: "Ex: Points Tempo Défense 2024",
            channels: "Salons associés",
            selectChannels: "Sélectionner les salons",
            timeTiers: "Tranches de temps",
            tierConfig: "Configuration des tranches",
            timeRange: "Durée du combat",
            points: "Points accordés",
            min0to5: "0-5 minutes",
            min5to10: "5-10 minutes",
            min10to15: "10-15 minutes",
            min15to20: "15-20 minutes",
            min20plus: "20+ minutes",
            timeInterval: "Intervalle de temps",
            timeRanges: "Tranches de Temps et Points",
            addTier: "Ajouter une tranche",
            removeTier: "Supprimer cette tranche",
            addFirstTier: "Ajouter la première tranche",
            noTiers: "Aucune tranche de temps définie",
            minTime: "Min (min)",
            maxTime: "Max (min)",
            maxTimeHint: "Laisser vide pour \"illimité\"",
            channel: "Salon Discord",
            selectChannel: "Sélectionner un salon",
            general: "Informations Générales",
            reset: "Réinitialiser",
            confirmUnsaved: "⚠️ Vous avez des modifications non sauvegardées. Les abandonner ?",
            createNew: "Nouvelle Configuration Tempo",
            createFirst: "Créer la première",
            loading: "Chargement des configurations tempo...",
            saveError: "❌ Erreur lors de la sauvegarde !",
            deleteError: "❌ Erreur lors de la suppression !",
            noConfigs: "Aucune configuration tempo créée",
            noChannelsSelected: "Aucun canal sélectionné",
            save: "Sauvegarder",
            saving: "Sauvegarde en cours...",
            create: "Créer",
            delete: "Supprimer",
            edit: "Modifier",
            nameRequired: "Le nom de la configuration est obligatoire",
            configCreated: "Configuration tempo créée avec succès !",
            configUpdated: "Configuration tempo mise à jour avec succès !",
            configDeleted: "Configuration tempo supprimée avec succès !",
            errorSaving: "Erreur lors de la sauvegarde !",
            errorDeleting: "Erreur lors de la suppression !",
            confirmDelete: "Voulez-vous vraiment supprimer la configuration \"{name}\" ?\n\nCette action est irréversible !",
            loadingConfigs: "Chargement des configurations...",
            calculation: "Calcul des points",
            combatDuration: "Durée du combat",
            pointsAwarded: "Points accordés",
            noConfigsAvailable: "Aucune configuration disponible"
        },
        player: {
            title: "Joueur",
            profile: "Profil",
            statistics: "Statistiques",
            history: "Historique",
            totalPoints: "Points totaux",
            currentRank: "Rang actuel",
            participations: "Participations",
            victories: "Victoires",
            winRate: "Taux de victoire",
            averagePoints: "Points moyens",
            bestRank: "Meilleur rang",
            lastActivity: "Dernière activité",
            points: "points",
            attackDetails: "Détails des attaques",
            defenseDetails: "Détails des défenses",
            noAttacks: "Aucune attaque enregistrée.",
            noDefenses: "Aucune défense enregistrée.",            errorLoadingData: "Erreur : Impossible de récupérer les données du joueur."
        },
        botNotPresent: {
            title: "Bot non présent sur {serverName}",
            subtitle: "Le bot de gestion n'est pas installé sur ce serveur Discord.",
            whyTitle: "Pourquoi cette page ?",
            reasons: {
                notInstalled: {
                    title: "Bot non installé",
                    description: "Le bot n'a pas été ajouté à ce serveur Discord"
                },
                insufficientPermissions: {
                    title: "Permissions insuffisantes", 
                    description: "Le bot n'a pas les permissions nécessaires sur ce serveur"
                },
                botRemoved: {
                    title: "Bot retiré",
                    description: "Le bot a été supprimé du serveur après votre dernière connexion"
                }
            },
            whatToDoTitle: "Que faire ?",
            actions: {
                inviteBot: {
                    title: "Ajouter le bot",
                    description: "Invitez le bot sur votre serveur Discord avec les permissions appropriées",
                    button: "Inviter le bot"
                },                contactAdmin: {
                    title: "Contacter un administrateur",
                    description: "Demandez à un administrateur de l'aide.",
                    button: "Ouvrir Discord"
                },
                checkPermissions: {
                    title: "Vérifier les permissions",
                    description: "Assurez-vous que le bot a les permissions nécessaires",
                    button: "Voir les permissions"
                }            },
            tryAgain: "Réessayer",
            contactSupport: "Support",
            helpTitle: "Besoin d'aide ?",
            helpDescription: "Si vous rencontrez des difficultés, consultez la documentation ou contactez le support.",
            documentation: "Documentation"
        },
        errors: {
            generic: "Une erreur est survenue",
            network: "Erreur de connexion réseau",
            unauthorized: "Accès non autorisé",
            forbidden: "Accès interdit",
            notFound: "Ressource non trouvée",
            serverError: "Erreur serveur interne",
            timeout: "Délai d'attente dépassé",
            invalidData: "Données invalides",
            sessionExpired: "Session expirée, veuillez vous reconnecter"
        },
        success: {
            saved: "Sauvegardé avec succès",
            created: "Créé avec succès",
            updated: "Mis à jour avec succès",
            deleted: "Supprimé avec succès"
        }
    },
    en: {        common: {
            loading: "Loading...",
            error: "Error",
            save: "Save",
            cancel: "Cancel",
            edit: "Edit",
            delete: "Delete",
            add: "Add",
            back: "Back",
            confirm: "Confirm",
            yes: "Yes",
            no: "No",
            search: "Search",
            filter: "Filter",
            settings: "Settings",
            logout: "Logout",
            welcome: "Welcome",
            name: "Name",
            description: "Description",
            date: "Date",
            time: "Time",
            status: "Status",
            actions: "Actions",
            submit: "Submit",
            reset: "Reset",
            close: "Close",
            open: "Open",
            select: "Select",
            none: "None",
            all: "All",
            from: "From",
            to: "To",
            at: "at",
            refresh: "Refresh",
            unknown: "Unknown",
            noResults: "No results",
            clearFilters: "Clear filters",
            irreversible: "This action is irreversible"
        },
        seasonsPage: {
            title: "Seasons",
            subtitle: "Create and manage your GDC seasons",
            create: "Create a season",
            merge: "Merge rankings",
            filterAll: "All",
            filterActive: "In progress",
            filterUpcoming: "Upcoming",
            filterFinished: "Finished",
            sortLabel: "Sort",
            sortNewest: "Newest first",
            sortOldest: "Oldest first",
            sortEnd: "End date",
            sortTitle: "Title",
            sortModified: "Modified by",
            colSeason: "Season",
            colStatus: "Status",
            colPeriod: "Period",
            colModifiedBy: "Modified by",
            colMemberStats: "Member stats",
            autoPublish: "Auto publish",
            open: "Open",
            archive: "Archive",
            unarchive: "Unarchive",
            archivedSection: "Archived seasons ({n})",
            archivedHint: "Archived seasons are kept intact, just tucked away here.",
            archived: "Season archived",
            unarchived: "Season unarchived",
            archiveError: "Could not change archiving",
            hideFromStats: "Hide from member stats",
            showInStats: "Show in member stats"
        },
        adminHome: {
            collapse: "Collapse menu",
            expand: "Expand menu",
            openMenu: "Open menu",
            closeMenu: "Close menu",
            backToServers: "My servers",
            adminArea: "Administration",
            currentSeason: "Current season",
            noSeason: "No season in progress",
            noSeasonHint: "Start a season to begin a new ranking.",
            createSeason: "Create a season",
            viewSeason: "Open season",
            daysLeft: "{n} days left",
            lastDay: "Last day",
            rankedPlayers: "{n} ranked players",
            top3: "Top 3",
            pts: "pts",
            unknownPlayer: "Unknown player",
            noRanking: "No points yet this season.",
            statsTitle: "Server at a glance",
            pepites: "Pépites collected",
            pepitesCurrent: "Current pépites",
            pepitesCurrentDetail: "Since the last distribution",
            pepitesTotalDetail: "All time, distributions included",
            statsError: "Could not load server figures.",
            pagesTitle: "Manage the server"
        },
        navigation: {
            dashboard: "Dashboard",
            seasons: "Seasons",
            events: "Events",
            players: "Players",
            configuration: "Configuration",
            bareme: "Scoring",
            tempo: "Tempo",
            reservation: "Reservation",
            statistics: "Statistics",
            pepites: "Pépites",
            language: "Language",
            backToServerList: "Back to Server List",
            backToServerListDescription: "Return to server selection"
        },
        login: {
            title: "Discord Login",
            subtitle: "Sign in with your Discord account",
            button: "Sign in with Discord",
            processing: "Signing in...",
            error: "Login error"
        },
        welcome: {
            title: "Welcome!",
            subtitle: "Select a server to get started",
            noServers: "No servers available",
            selectServer: "Select server",
            adminAccess: "Administrator",
            memberAccess: "Member Space",
            hello: "Hi, {name}",
            searchPlaceholder: "Search a server",
            noMatch: "No server matches “{term}”",
            noAccess: "The bot isn't on any of your Discord servers yet.",
            roleAdmin: "Admin",
            roleMember: "Member",
            openAdmin: "Manage",
            favoriteAdd: "Add to favorites",
            favoriteRemove: "Remove from favorites"
        },
        member: {
            badge: "Member Space",
            hello: "Hello",
            welcomeText: "Welcome to your personal space.",
            preferencesTitle: "My zone preferences",
            preferencesDesc: "Rank the Dofus zones in order of preference for reservation sessions.",
            statsTitle: "My statistics",
            statsDesc: "Your GDC performance: attacks, defenses, points earned.",
            reservationTitle: "My reservation",
            reservationDesc: "View your reserved zone for the current session.",
            comingSoon: "Coming soon"
        },        dashboard: {
            title: "Dashboard",
            serverConfig: "Server Configuration",
            seasonManagement: "Season Management",
            eventManagement: "Event Management",
            reservationManagement: "Reservation Management",
            statisticsManagement: "Player Statistics",
            pepitesManagement: "Alliance Pépites",
            scoreSettings: "Score Settings",
            tempoManagement: "Tempo Points Management",
            // New content for AccueilPage
            welcomeTitle: "{serverName} Dashboard",
            welcomeSubtitle: "Manage your Discord server and GDC events",
            language: "Language",
            statistics: "Statistics",
            quickStats: {
                configuredChannels: "Configured Channels",
                baremes: "Scoring Systems",
                seasons: "Seasons",
                events: "Events"
            },
            statsCards: {
                server: {
                    title: "Server",
                    totalChannels: "Total channels:",
                    configuredChannels: "Configured channels:",
                    availableRoles: "Available roles:"
                },
                baremes: {
                    title: "Scoring Systems",
                    created: "Scoring systems created",
                    manage: "Manage scoring systems"
                },
                seasons: {
                    title: "Seasons",
                    created: "Seasons created",
                    manage: "Manage seasons"
                },
                events: {
                    title: "Events",
                    planned: "Events planned",
                    manage: "Manage events"
                }
            },
            quickActions: {
                title: "Quick Actions",
                configureChannels: {
                    title: "Configure Channels",
                    description: "Associate your Discord channels with attack/defense types"
                },
                createBareme: {
                    title: "Create Scoring System",
                    description: "Define attack and defense points"
                },
                launchSeason: {
                    title: "Launch Season",
                    description: "Create a new GDC season with rankings"
                },
                planEvent: {
                    title: "Plan Event",
                    description: "Organize your clan wars and tournaments"
                }
            },
            serverStatus: "Online"
        },
        seasons: {
            title: "Season Management",
            createNew: "Create new season",
            noSeasons: "No seasons found",
            currentSeason: "Current season",
            startDate: "Start date",
            endDate: "End date",
            active: "Active",
            inactive: "Inactive",
            upcoming: "Upcoming",
            finished: "Finished",
            archived: "Archived",
            seasonsPlural: "seasons",
            seasonSingular: "season",
            allSeasons: "All",
            loading: "Loading seasons",
            createFirstSeason: "Create first season",
            noResultsMessage: "No seasons match your search criteria",
            autoPublishEnabled: "Auto-publish enabled",
            modifiedBy: "Modified by",
            edit: "Edit season",
            delete: "Delete season",
            confirmDelete: "Are you sure you want to delete this season?",
            deleteSuccess: "Season deleted successfully",
            deleteError: "Error deleting season"
        },
        createSeason: {
            title: "Create new season",
            progress: "Progress",
            name: "Season name",
            namePlaceholder: "Ex: Summer Season 2024",
            description: "Description",
            descriptionPlaceholder: "Season description...",
            startDate: "Start date",
            endDate: "End date",
            create: "Create season",
            creating: "Creating...",
            success: "Season created successfully!",
            error: "Error creating season",
            configurationSection: "Scoring Configuration",
            attackBareme: "Attack Scoring",
            defenseBareme: "Defense Scoring",
            selectAttackBareme: "Select an attack scoring system",
            selectDefenseBareme: "Select a defense scoring system",
            noAttackBareme: "No attack scoring system available",
            noDefenseBareme: "No defense scoring system available",
            publicationChannel: "Publication Channel",
            selectChannel: "Select a channel",
            seasonPeriod: "Season Period",
            descriptionAndOptions: "Description and Options",
            descriptionPlaceholderLong: "Describe the rules and objectives of this season...",
            autoPublish: "Automatic ranking publication at season end",
            requiredFields: "Required fields",
            createdBy: "Created by",
            cancel: "Cancel",
            createSeason: "Create Season",
            validation: {
                nameRequired: "Name is required",
                startDateRequired: "Start date is required",
                endDateRequired: "End date is required",
                publicationChannelRequired: "Publication channel is required",
                attackBaremeRequired: "Attack scoring system is required",
                defenseBaremeRequired: "Defense scoring system is required",
                descriptionRequired: "Description is required",
                endDateAfterStart: "End date must be after start date"
            }
        },editSeason: {
            title: "Edit season",
            updating: "Updating...",
            success: "Season updated successfully!",
            error: "Error updating season",
            notFound: "Season not found",
            configuration: "Configuration",
            ranking: "Ranking",
            seasonName: "Season Name",
            startDate: "Start date",
            endDate: "End date",
            attackBareme: "Attack Scale",
            defenseBareme: "Defense Scale",
            selectBareme: "Select a scale",
            autoPublish: "Automatic ranking publication at season end",
            save: "Save",
            refresh: "Refresh",
            publish: "Publish",
            cancel: "Cancel",
            loadingError: "Season loading error",
            seasonNotFound: "Season not found!",
            channelsError: "Channels retrieval error",
            baremesError: "Scales retrieval error",
            rankingError: "Ranking error",
            saveModifications: "Save mods",
            usernames: "Usernames",
            players: "players",
            modifications: "mods",
            loadingUsernames: "Loading usernames",
            player: "Player",
            attackDefense: "Att/Def",
            attack: "Attack",
            defense: "Defense",
            tempo: "Tempo",
            points: "Points",actions: "Actions",
            total: "Total",
            events: "Events",
            rank: "Rank",
            saveModificationsSuccess: "Modifications saved!",
            saveModificationsFailed: "Save failed!",
            recalculateConfirm: "Do you want to recalculate the points for this season taking into account active events?",
            recalculateSuccess: "Season successfully recalculated with events taken into account!",
            recalculateError: "Error during season recalculation with events!",
            seasonAndModificationsSaved: "Season and modifications saved!",
            updateError: "Update error!",
            playerHistoryError: "Unable to load player history.",
            playerNotFoundWarning: "Player not found in DB, local fallback.",            refreshUsernamesSuccess: "usernames updated successfully",
            refreshUsernamesPartial: "Refresh completed: {success} names updated, {error} failures. Retry if necessary.",
            refreshUsernamesError: "Error refreshing usernames!",
            refreshUsernames: "Usernames",
            refreshUsernamesTooltip: "Refresh all usernames from Discord",
            backToSeasons: "Back to seasons",
            dashboard: "Dashboard",
            playerModifications: "Modifications of",
            playerIdShort: "Player",
            noModifications: "No modifications recorded.",
            by: "by",
            on: "on",
            pts: "pts",
            publishRanking: "Publish ranking",
            selectChannel: "Please select a publication channel:",
            channelsAvailable: "channel(s) available",
            noChannelsAvailable: "No channels available",
            serverId: "Server ID",
            publicationChannel: "Publication channel:",
            noRankingData: "No ranking data to publish!",
            publishButton: "Publish",
            noChannelButton: "No channel",
            selectChannelButton: "Select channel",
            noChannelSelected: "No channel selected",
            refreshDataError: "Error refreshing data!"
        },        events: {
            title: "Event Management",
            subtitle: "Create and manage your server's special events",
            loading: "Loading events...",
            loadingError: "Loading error",
            retry: "Retry",
            addEvent: "Add event",
            noEvents: "No events configured",
            eventName: "Event name",
            eventType: "Event type",
            salon: "Channel",
            addSalon: "Add channel",
            removeSalon: "Remove channel",
            save: "Save events",
            saving: "Saving...",
            success: "Events saved successfully!",
            error: "Error saving events",
            validation: {
                nameRequired: "Event name is required",
                typeRequired: "Event type is required",
                salonRequired: "At least one channel is required"
            },            // Event configuration
            tabs: {
                list: "📋 Event list ({count})",
                create: "➕ Create event",
                edit: "✏️ Edit"
            },            emptyState: {
                title: "📭 No events created",
                description: "Create your first event to get started!",
                createButton: "➕ Create event"
            },            table: {
                status: "Status",
                event: "Event",
                type: "Type",
                period: "Period",
                config: "Configuration",
                actions: "Actions",
                types: "type(s)"
            },            status: {
                active: "🟢 Active",
                scheduled: "🟡 Scheduled",
                inactive: "🔴 Inactive",
                activated: "activated",
                deactivated: "deactivated"
            },            form: {
                createTitle: "➕ Create new event",
                editTitle: "✏️ Edit event",
                name: "Event name",
                namePlaceholder: "Ex: Double Points Weekend",
                type: "Event type",
                description: "Description",
                descriptionPlaceholder: "Event description...",
                startDate: "Start date",
                endDate: "End date",
                isActive: "Event active"
            },
            types: {
                pointsMultiplier: {
                    name: "Points Multiplier",
                    description: "Double or triple points during a period"
                },
                specialLadder: {
                    name: "Special Ladder",
                    description: "Special ranking with particular rules"
                },
                combatRestriction: {
                    name: "Combat Restriction",
                    description: "Event limited to certain combat types"                },
                unknownType: "Unknown type"
            },            config: {
                multiplierTitle: "⚡ Multiplier Configuration",
                ladderTitle: "🏆 Special Ladder Configuration",
                restrictionTitle: "⚔️ Combat Restriction Configuration",
                multiplier: "Multiplier",
                applyToAttack: "Apply to attack points",
                applyToDefense: "Apply to defense points",
                allowedCombatTypes: "Allowed combat types",
                excludeMultipleParticipation: "Exclude multiple participations in the same combat",
                minParticipants: "Minimum participants",
                maxParticipants: "Maximum participants"            },            actions: {
                edit: "Edit event",
                activate: "Activate",
                deactivate: "Deactivate",
                delete: "Delete event",
                cancel: "❌ Cancel",
                create: "➕ Create",
                update: "💾 Update",
                saving: "⏳ Saving...",
                retry: "🔄 Retry"
            },            messages: {
                nameRequired: "Event name is required!",
                datesRequired: "Start and end dates are required!",
                createSuccess: "✅ Event created successfully!",
                updateSuccess: "✅ Event updated successfully!",
                deleteSuccess: "✅ Event deleted successfully!",
                toggleSuccess: "✅ Event {status}!",
                saveError: "❌ Error saving!",
                deleteError: "❌ Error deleting!",
                toggleError: "❌ Error changing status!",                loadingError: "Loading error",
                loadingErrorTitle: "❌ Loading error",
                confirmDelete: "⚠️ Are you sure you want to delete this event?"
            },
            dates: {
                from: "From",
                to: "To"
            },
            multipliers: {
                double: "x2 (Double)",
                triple: "x3 (Triple)"
            }        },
        reservation: {
            subtitle: "Manage your server's reservations",
            configTitle: "Bot configuration",
            configSubtitle: "Set the Discord channel where /pos will be active and the related season.",
            channelLabel: "Reservation channel",
            channelHint: "Players will use /pos in this channel",
            selectChannel: "-- Select a channel --",
            activeSaisonLabel: "Active season",
            activeSaisonHint: "This season's ranking is used for /pos",
            selectActiveSaison: "-- Select a season --",
            tableTitle: "Reservations",
            addRow: "Add a row",
            colSaison: "Season",
            colJoueur: "Player",
            colZone: "Zone",
            selectSaison: "-- Choose a season --",
            selectJoueur: "-- Choose a player --",
            selectZone: "-- Zone --",
            selectSaisonFirst: "Select a season first",
            noPlayers: "No players in this season",
            autoAssign: "Auto-assign zones",
            noPreference: "No preference",
            sessionTitle: "Reservation sheet",
            sessionSubtitle: "Select or create a sheet to organize reservations.",
            selectSessionLabel: "Active sheet",
            selectSession: "-- Select a sheet --",
            newSession: "New sheet",
            newSessionPlaceholder: "Ex: GDC Week 1",
            createSession: "Create sheet",
            saveRows: "Save sheet",
            comingSoon: "Coming Soon",
            comingSoonDescription: "Reservation management will be available soon. Stay tuned!"
        },
        adminStats: {
            title: "Player Statistics",
            subtitle: "Unique attacks and defenses per player",
            season: "Season",
            allSeasons: "All seasons",
            searchPlaceholder: "Search a player...",
            colName: "Name",
            colAttacks: "Unique Attacks",
            colDefenses: "Unique Defenses",
            noPlayers: "No players found",
            playersCount: "player(s) displayed",
            showZeroLabel: "Inactive players",
            showZeroPlayers: "Show players with 0 points"
        },
        bareme: {
            title: "Scoring Configuration",
            subtitle: "Configuration of attack and defense points",
            existingBaremes: "Existing Scoring Systems",
            newBareme: "New",
            createBareme: "Create new scoring system",
            editBareme: "Edit scoring system",
            baremeName: "Scoring system name",
            baremeNamePlaceholder: "Ex: GDC Summer 2024 Scoring",
            type: "Type",
            attack: "Attack",
            defense: "Defense",
            search: "Search...",
            allTypes: "All types",
            noBaremes: "No scoring systems created",
            noResults: "No results",
            completion: "Completed",
            progression: "Progress",
            totalBaremes: "Total Scoring Systems",
            unsavedChanges: "Unsaved changes",
            reset: "Cancel",
            save: "Save",
            create: "Create",
            saving: "Saving...",
            delete: "Delete",
            enemy: "enemy",
            enemies: "enemies",
            allies: "allies",
            ally: "ally",
            loadingBaremes: "Loading scoring systems...",
            nameRequired: "Please enter a scoring system name!",
            baremeUpdated: "Scoring system updated successfully!",
            baremeCreated: "Scoring system created successfully!",
            baremeDeleted: "Scoring system deleted successfully!",
            errorSaving: "Error saving!",
            errorDeleting: "Error deleting!",
            confirmDelete: "Do you really want to delete the scoring system \"{name}\"?\n\nThis action is irreversible!",
            confirmUnsaved: "You have unsaved changes. Discard them?",
            addRule: "Add rule",
            noRules: "No rules configured",
            rank: "Rank",
            points: "Points",
            description: "Description",
            success: "Scoring saved successfully!",
            error: "Error saving scoring"
        },
        tempo: {
            title: "Tempo Points Configuration",
            subtitle: "Reward defensive combats that make opponents lose time",
            existingConfigs: "Existing Configurations",
            newConfig: "New",
            createConfig: "Create new configuration",
            editConfig: "Edit configuration",
            configName: "Configuration name",
            configNamePlaceholder: "Ex: Tempo Defense Points 2024",
            channels: "Associated channels",
            selectChannels: "Select channels",
            timeTiers: "Time tiers",
            tierConfig: "Tier configuration",
            timeRange: "Combat duration",
            points: "Points awarded",
            min0to5: "0-5 minutes",
            min5to10: "5-10 minutes",
            min10to15: "10-15 minutes", 
            min15to20: "15-20 minutes",
            min20plus: "20+ minutes",
            timeInterval: "Time interval",
            timeRanges: "Time Ranges and Points",
            addTier: "Add tier",
            removeTier: "Remove this tier",
            addFirstTier: "Add first tier",
            noTiers: "No time tiers defined",
            minTime: "Min (min)",
            maxTime: "Max (min)",
            maxTimeHint: "Leave empty for \"unlimited\"",
            channel: "Discord Channel",
            selectChannel: "Select a channel",
            general: "General Information",
            reset: "Reset",
            confirmUnsaved: "⚠️ You have unsaved changes. Discard them?",
            createNew: "New Tempo Configuration",
            createFirst: "Create first one",
            loading: "Loading tempo configurations...",
            saveError: "❌ Error saving!",
            deleteError: "❌ Error deleting!",
            noConfigs: "No tempo configurations created",
            noChannelsSelected: "No channels selected",
            save: "Save",
            saving: "Saving...",
            create: "Create",
            delete: "Delete",
            edit: "Edit",
            nameRequired: "Configuration name is required",
            configCreated: "Tempo configuration created successfully!",
            configUpdated: "Tempo configuration updated successfully!",
            configDeleted: "Tempo configuration deleted successfully!",
            errorSaving: "Error saving!",
            errorDeleting: "Error deleting!",
            confirmDelete: "Do you really want to delete the configuration \"{name}\"?\n\nThis action is irreversible!",
            loadingConfigs: "Loading configurations...",
            calculation: "Points calculation",
            combatDuration: "Combat duration",
            pointsAwarded: "Points awarded",
            noConfigsAvailable: "No configurations available"
        },
        player: {
            title: "Player",
            profile: "Profile",
            statistics: "Statistics",
            history: "History",
            totalPoints: "Total points",
            currentRank: "Current rank",
            participations: "Participations",
            victories: "Victories",
            winRate: "Win rate",
            averagePoints: "Average points",
            bestRank: "Best rank",
            lastActivity: "Last activity",
            points: "points",
            attackDetails: "Attack details",
            defenseDetails: "Defense details",
            noAttacks: "No attacks recorded.",
            noDefenses: "No defenses recorded.",            errorLoadingData: "Error: Unable to retrieve player data."
        },
        botNotPresent: {
            title: "Bot not present on {serverName}",
            subtitle: "The management bot is not installed on this Discord server.",
            whyTitle: "Why this page?",
            reasons: {
                notInstalled: {
                    title: "Bot not installed",
                    description: "The bot has not been added to this Discord server"
                },
                insufficientPermissions: {
                    title: "Insufficient permissions", 
                    description: "The bot does not have the necessary permissions on this server"
                },
                botRemoved: {
                    title: "Bot removed",
                    description: "The bot was removed from the server after your last connection"
                }
            },
            whatToDoTitle: "What to do?",
            actions: {
                inviteBot: {
                    title: "Add the bot",
                    description: "Invite the bot to your Discord server with appropriate permissions",
                    button: "Invite bot"
                },                contactAdmin: {
                    title: "Contact an administrator",
                    description: "Ask an administrator for help.",
                    button: "Open Discord"
                },
                checkPermissions: {
                    title: "Check permissions",
                    description: "Make sure the bot has the necessary permissions",
                    button: "View permissions"
                }            },
            tryAgain: "Try again",
            contactSupport: "Support",
            helpTitle: "Need help?",
            helpDescription: "If you encounter difficulties, check the documentation or contact support.",
            documentation: "Documentation"
        },
        server: {
            title: "Server Configuration",
            subtitle: "Association of Discord channels with attack and defense types",
            serverName: "Server name",
            serverIcon: "Server icon",
            settings: "General settings",
            permissions: "Permissions",
            roles: "Roles",
            channels: "Channels",
            generalConfig: "General Configuration",
            configName: "Configuration name",
            configNamePlaceholder: "Ex: GDC Summer 2024 Configuration",
            specialRole: "Manager role",
            noSpecialRole: "No special role",
            channelsAssociation: "Channel Association",
            attackConfig: "Attack Configuration",
            defenseConfig: "Defense Configuration",
            tempoConfig: "Tempo Configuration",
            against: "Against",
            selectChannel: "Select a channel",
            instructions: "Instructions",
            attackInstruction: "Select channels where members will post their attacks based on the number of enemies defeated.",
            defenseInstruction: "Select channels where members will post their defenses based on the number of enemies repelled.",
            tempoInstruction: "Select the channel where members will post their defensive combats for tempo points.",
            specialRoleInstruction: "Optional - Role that will have special permissions in the system.",
            discordChannels: "Discord Channels",
            configuredChannels: "Configured Channels",
            availableRoles: "Available Roles",
            configuration: "Configuration",
            progression: "Progress",
            reset: "Reset",
            save: "Save configuration",
            saving: "Saving...",
            unsavedChanges: "You have unsaved changes",
            loadingConfig: "Loading configuration...",
            configNameRequired: "Please enter a configuration name!",
            configSaved: "Configuration saved successfully!",
            configSaveFailed: "Save failed!",
            success: "Configuration saved successfully!",
            error: "Error saving configuration",
            confirmReset: "Do you really want to reset the configuration?"
        },
        errors: {
            generic: "An error occurred",
            network: "Network connection error",
            unauthorized: "Unauthorized access",
            forbidden: "Access forbidden",
            notFound: "Resource not found",
            serverError: "Internal server error",
            timeout: "Timeout exceeded",
            invalidData: "Invalid data",
            sessionExpired: "Session expired, please log in again"
        },
        success: {
            saved: "Saved successfully",
            created: "Created successfully",
            updated: "Updated successfully",
            deleted: "Deleted successfully"
        }
    },
    es: {        common: {
            loading: "Cargando...",
            error: "Error",
            save: "Guardar",
            cancel: "Cancelar",
            edit: "Editar",
            delete: "Eliminar",
            add: "Agregar",
            back: "Volver",
            confirm: "Confirmar",
            yes: "Sí",
            no: "No",
            search: "Buscar",
            filter: "Filtrar",
            settings: "Configuración",
            logout: "Cerrar sesión",
            welcome: "Bienvenido",
            name: "Nombre",
            description: "Descripción",
            date: "Fecha",
            time: "Hora",
            status: "Estado",
            actions: "Acciones",
            submit: "Enviar",
            reset: "Restablecer",
            close: "Cerrar",
            open: "Abrir",
            select: "Seleccionar",
            none: "Ninguno",
            all: "Todos",
            from: "De",
            to: "A",
            at: "a",
            refresh: "Actualizar",
            unknown: "Desconocido",
            noResults: "Sin resultados",
            clearFilters: "Limpiar filtros",
            irreversible: "Esta acción es irreversible"
        },
        seasonsPage: {
            title: "Temporadas",
            subtitle: "Crea y gestiona tus temporadas de GDC",
            create: "Crear una temporada",
            merge: "Fusionar clasificaciones",
            filterAll: "Todas",
            filterActive: "En curso",
            filterUpcoming: "Próximas",
            filterFinished: "Terminadas",
            sortLabel: "Ordenar",
            sortNewest: "Más recientes",
            sortOldest: "Más antiguas",
            sortEnd: "Fecha de fin",
            sortTitle: "Título",
            sortModified: "Modificado por",
            colSeason: "Temporada",
            colStatus: "Estado",
            colPeriod: "Periodo",
            colModifiedBy: "Modificado por",
            colMemberStats: "Stats miembros",
            autoPublish: "Publicación auto",
            open: "Abrir",
            archive: "Archivar",
            unarchive: "Desarchivar",
            archivedSection: "Temporadas archivadas ({n})",
            archivedHint: "Las temporadas archivadas se conservan intactas, solo se guardan aquí.",
            archived: "Temporada archivada",
            unarchived: "Temporada desarchivada",
            archiveError: "No se pudo cambiar el archivado",
            hideFromStats: "Ocultar de las stats de miembros",
            showInStats: "Mostrar en las stats de miembros"
        },
        adminHome: {
            collapse: "Contraer el menú",
            expand: "Expandir el menú",
            openMenu: "Abrir el menú",
            closeMenu: "Cerrar el menú",
            backToServers: "Mis servidores",
            adminArea: "Administración",
            currentSeason: "Temporada en curso",
            noSeason: "Ninguna temporada en curso",
            noSeasonHint: "Inicia una temporada para comenzar una nueva clasificación.",
            createSeason: "Crear una temporada",
            viewSeason: "Abrir la temporada",
            daysLeft: "Quedan {n} días",
            lastDay: "Último día",
            rankedPlayers: "{n} jugadores clasificados",
            top3: "Top 3",
            pts: "pts",
            unknownPlayer: "Jugador desconocido",
            noRanking: "Aún no hay puntos esta temporada.",
            statsTitle: "El servidor en cifras",
            pepites: "Pépites recolectadas",
            pepitesCurrent: "Pépites en curso",
            pepitesCurrentDetail: "Desde la última distribución",
            pepitesTotalDetail: "En total, distribuciones incluidas",
            statsError: "No se pudieron cargar las cifras del servidor.",
            pagesTitle: "Gestionar el servidor"
        },
        navigation: {
            dashboard: "Panel de control",
            seasons: "Temporadas",
            events: "Eventos",
            players: "Jugadores",
            configuration: "Configuración",
            bareme: "Puntuación",
            tempo: "Tempo",
            reservation: "Reserva",
            statistics: "Estadísticas",
            pepites: "Pépites",
            language: "Idioma",
            backToServerList: "Volver a la Lista de Servidores",
            backToServerListDescription: "Regresar a la selección de servidor"
        },
        login: {
            title: "Inicio de sesión Discord",
            subtitle: "Inicia sesión con tu cuenta de Discord",
            button: "Iniciar sesión con Discord",
            processing: "Iniciando sesión...",
            error: "Error de inicio de sesión"
        },
        welcome: {
            title: "¡Bienvenido!",
            subtitle: "Selecciona un servidor para comenzar",
            noServers: "No hay servidores disponibles",
            selectServer: "Seleccionar servidor",
            adminAccess: "Administrador",
            memberAccess: "Espacio Miembro",
            hello: "Hola, {name}",
            searchPlaceholder: "Buscar un servidor",
            noMatch: "Ningún servidor coincide con «{term}»",
            noAccess: "El bot aún no está en ninguno de tus servidores de Discord.",
            roleAdmin: "Admin",
            roleMember: "Miembro",
            openAdmin: "Administrar",
            favoriteAdd: "Añadir a favoritos",
            favoriteRemove: "Quitar de favoritos"
        },
        member: {
            badge: "Espacio Miembro",
            hello: "Hola",
            welcomeText: "Bienvenido a tu espacio personal.",
            preferencesTitle: "Mis preferencias de zonas",
            preferencesDesc: "Clasifica las zonas de Dofus en orden de preferencia para las sesiones de reserva.",
            statsTitle: "Mis estadísticas",
            statsDesc: "Tu rendimiento en los GDC: ataques, defensas, puntos obtenidos.",
            reservationTitle: "Mi reserva",
            reservationDesc: "Consulta tu zona reservada para la sesión actual.",
            comingSoon: "Próximamente"
        },        dashboard: {
            title: "Panel de control",
            serverConfig: "Configuración del servidor",
            seasonManagement: "Gestión de temporadas",
            eventManagement: "Gestión de eventos",
            reservationManagement: "Gestión de reservas",
            statisticsManagement: "Estadísticas de jugadores",
            pepitesManagement: "Gestión de pépites",
            scoreSettings: "Configuración de puntuación",
            tempoManagement: "Gestión de puntos tempo",
            // New content for AccueilPage
            welcomeTitle: "Panel de control de {serverName}",
            welcomeSubtitle: "Gestiona tu servidor Discord y eventos GDC",
            language: "Idioma",
            statistics: "Estadísticas",
            quickStats: {
                configuredChannels: "Canales configurados",
                baremes: "Sistemas de puntuación",
                seasons: "Temporadas",
                events: "Eventos"
            },
            statsCards: {
                server: {
                    title: "Servidor",
                    totalChannels: "Canales totales:",
                    configuredChannels: "Canales configurados:",
                    availableRoles: "Roles disponibles:"
                },
                baremes: {
                    title: "Sistemas de Puntuación",
                    created: "Sistemas de puntuación creados",
                    manage: "Gestionar sistemas de puntuación"
                },
                seasons: {
                    title: "Temporadas",
                    created: "Temporadas creadas",
                    manage: "Gestionar temporadas"
                },
                events: {
                    title: "Eventos",
                    planned: "Eventos planificados",
                    manage: "Gestionar eventos"
                }
            },
            quickActions: {
                title: "Acciones Rápidas",
                configureChannels: {
                    title: "Configurar Canales",
                    description: "Asocia tus canales Discord con tipos de ataque/defensa"
                },
                createBareme: {
                    title: "Crear Sistema de Puntuación",
                    description: "Define puntos de ataque y defensa"
                },
                launchSeason: {
                    title: "Lanzar Temporada",
                    description: "Crea una nueva temporada GDC con clasificaciones"
                },
                planEvent: {
                    title: "Planificar Evento",
                    description: "Organiza tus guerras de clanes y torneos"
                }
            },
            serverStatus: "En línea"
        },
        seasons: {
            title: "Gestión de Temporadas",
            createNew: "Crear nueva temporada",
            noSeasons: "No se encontraron temporadas",
            currentSeason: "Temporada actual",
            startDate: "Fecha de inicio",
            endDate: "Fecha de fin",
            active: "Activa",
            inactive: "Inactiva",
            edit: "Editar temporada",
            delete: "Eliminar temporada",
            modifiedBy: "Modificado por",
            confirmDelete: "¿Estás seguro de que quieres eliminar esta temporada?",
            deleteSuccess: "Temporada eliminada con éxito",
            deleteError: "Error al eliminar la temporada",
            upcoming: "Próxima",
            finished: "Finalizada",
            archived: "Archivada",
            seasonsPlural: "temporadas",
            seasonSingular: "temporada",
            allSeasons: "Todas"
        },
        createSeason: {
            title: "Crear nueva temporada",
            progress: "Progreso",
            name: "Nombre de la temporada",
            namePlaceholder: "Ej: Temporada Verano 2024",
            description: "Descripción",
            descriptionPlaceholder: "Descripción de la temporada...",
            startDate: "Fecha de inicio",
            endDate: "Fecha de fin",
            create: "Crear temporada",
            creating: "Creando...",
            success: "¡Temporada creada con éxito!",
            error: "Error al crear la temporada",
            configurationSection: "Configuración de Puntuación",
            attackBareme: "Puntuación de Ataque",
            defenseBareme: "Puntuación de Defensa",
            selectAttackBareme: "Seleccionar sistema de puntuación de ataque",
            selectDefenseBareme: "Seleccionar sistema de puntuación de defensa",
            noAttackBareme: "No hay sistema de puntuación de ataque disponible",
            noDefenseBareme: "No hay sistema de puntuación de defensa disponible",
            publicationChannel: "Canal de Publicación",
            selectChannel: "Seleccionar un canal",
            seasonPeriod: "Período de la Temporada",
            descriptionAndOptions: "Descripción y Opciones",
            descriptionPlaceholderLong: "Describe las reglas y objetivos de esta temporada...",
            autoPublish: "Publicación automática de la clasificación al final de la temporada",
            requiredFields: "Campos obligatorios",
            createdBy: "Creado por",
            cancel: "Cancelar",
            createSeason: "Crear Temporada",
            validation: {
                nameRequired: "El nombre es obligatorio",
                startDateRequired: "La fecha de inicio es obligatoria",
                endDateRequired: "La fecha de fin es obligatoria",
                publicationChannelRequired: "El canal de publicación es obligatorio",
                attackBaremeRequired: "El sistema de puntuación de ataque es obligatorio",
                defenseBaremeRequired: "El sistema de puntuación de defensa es obligatorio",
                descriptionRequired: "La descripción es obligatoria",
                endDateAfterStart: "La fecha de fin debe ser posterior a la fecha de inicio"
            }
        },editSeason: {
            title: "Editar temporada",
            updating: "Actualizando...",
            success: "¡Temporada actualizada con éxito!",
            error: "Error al actualizar la temporada",
            notFound: "Temporada no encontrada",
            configuration: "Configuración",
            ranking: "Clasificación",
            seasonName: "Nombre de la Temporada",
            startDate: "Fecha de inicio",
            endDate: "Fecha de fin",
            attackBareme: "Escala de Ataque",
            defenseBareme: "Escala de Defensa",
            selectBareme: "Seleccionar una escala",
            autoPublish: "Publicación automática de la clasificación al final de la temporada",
            save: "Guardar",
            refresh: "Actualizar",
            publish: "Publicar",
            cancel: "Cancelar",
            loadingError: "Error de carga de temporada",
            seasonNotFound: "¡Temporada no encontrada!",
            channelsError: "Error de recuperación de canales",
            baremesError: "Error de recuperación de escalas",
            rankingError: "Error de clasificación",
            saveModifications: "Guardar mods",
            usernames: "Usuarios",
            players: "jugadores",
            modifications: "mods",
            loadingUsernames: "Cargando nombres de usuario",
            player: "Jugador",
            attack: "Ataque",
            defense: "Defensa",
            tempo: "Tempo",
            points: "Puntos",            actions: "Acciones",
            total: "Total",
            events: "Eventos",
            rank: "Rango",
            saveModificationsSuccess: "¡Modifications guardadas!",
            saveModificationsFailed: "¡Error al guardar!",
            recalculateConfirm: "¿Quiere recalcular los puntos de esta temporada teniendo en cuenta los eventos activos?",
            recalculateSuccess: "¡Temporada recalculada con éxito teniendo en cuenta los eventos!",
            recalculateError: "¡Error durante el recálculo de la temporada con eventos!",
            seasonAndModificationsSaved: "¡Temporada y modificaciones guardadas!",
            updateError: "¡Error de actualización!",
            playerHistoryError: "No se puede cargar el historial del jugador.",
            playerNotFoundWarning: "Jugador no encontrado en la BD, respaldo local.",            refreshUsernamesSuccess: "nombres de usuario actualizados con éxito",            refreshUsernamesPartial: "Actualización completada: {success} nombres actualizados, {error} fallos. Reintenta si es necesario.",
            refreshUsernamesError: "¡Error al actualizar nombres de usuario!",
            refreshUsernames: "Usuarios",
            refreshUsernamesTooltip: "Actualizar todos los nombres de usuario desde Discord",
            backToSeasons: "Volver a temporadas",
            dashboard: "Panel de control",
            playerModifications: "Modifications de",
            playerIdShort: "Jugador",
            noModifications: "No hay modificaciones registradas.",
            by: "por",
            on: "el",
            pts: "pts",
            publishRanking: "Publicar clasificación",
            selectChannel: "Por favor selecciona un canal de publicación:",
            channelsAvailable: "canal(es) disponible(s)",
            noChannelsAvailable: "No hay canales disponibles",
            serverId: "ID del servidor",
            publicationChannel: "Canal de publicación:",
            noRankingData: "¡No hay datos de clasificación para publicar!",
            publishButton: "Publicar",
            noChannelButton: "Ningún canal",
            selectChannelButton: "Seleccionar canal",
            noChannelSelected: "Ningún canal seleccionado",
            refreshDataError: "¡Error al actualizar datos!"
        },        events: {
            title: "Gestión de Eventos",
            subtitle: "Crea y gestiona los eventos especiales de tu servidor",
            loading: "Cargando eventos...",
            loadingError: "Error de carga",
            retry: "Reintentar",
            addEvent: "Agregar evento",
            noEvents: "No hay eventos configurados",
            eventName: "Nombre del evento",
            eventType: "Tipo de evento",
            salon: "Canal",
            addSalon: "Agregar canal",
            removeSalon: "Eliminar canal",
            save: "Guardar eventos",
            saving: "Guardando...",
            success: "¡Eventos guardados con éxito!",
            error: "Error al guardar eventos",
            validation: {
                nameRequired: "El nombre del evento es obligatorio",
                typeRequired: "El tipo de evento es obligatorio",
                salonRequired: "Se requiere al menos un canal"            },
            // Configuración de eventos
            tabs: {
                list: "📋 Lista de eventos ({count})",
                create: "➕ Crear evento",
                edit: "✏️ Editar"
            },
            emptyState: {
                title: "📭 No hay eventos creados",
                description: "¡Crea tu primer evento para comenzar!",
                createButton: "➕ Crear evento"
            },            table: {
                status: "Estado",
                event: "Evento",
                type: "Tipo",
                period: "Período",
                config: "Configuración",
                actions: "Acciones",
                types: "tipo(s)"
            },            status: {
                active: "🟢 Activo",
                scheduled: "🟡 Programado",
                inactive: "🔴 Inactivo",
                activated: "activado",
                deactivated: "desactivado"
            },            form: {
                createTitle: "➕ Crear nuevo evento",
                editTitle: "✏️ Editar evento",
                name: "Nombre del evento",
                namePlaceholder: "Ej: Fin de Semana de Puntos Dobles",
                type: "Tipo de evento",
                description: "Descripción",
                descriptionPlaceholder: "Descripción del evento...",
                startDate: "Fecha de inicio",
                endDate: "Fecha de fin",
                isActive: "Evento activo"
            },
            types: {
                pointsMultiplier: {
                    name: "Multiplicador de Puntos",
                    description: "Duplica o triplica los puntos durante un período"
                },
                specialLadder: {
                    name: "Escalera Especial",
                    description: "Clasificación especial con reglas particulares"
                },
                combatRestriction: {
                    name: "Restricción de Combate",
                    description: "Evento limitado a ciertos tipos de combate"                },
                unknownType: "Tipo desconocido"
            },            config: {
                multiplierTitle: "⚡ Configuración del Multiplicador",
                ladderTitle: "🏆 Configuración de Escalera Especial", 
                restrictionTitle: "⚔️ Configuración de Restricción de Combate",
                multiplier: "Multiplicador",
                applyToAttack: "Aplicar a puntos de ataque",
                applyToDefense: "Aplicar a puntos de defensa",
                allowedCombatTypes: "Tipos de combate permitidos",
                excludeMultipleParticipation: "Excluir participaciones múltiples en el mismo combate",
                minParticipants: "Participantes mínimos",
                maxParticipants: "Participantes máximos"
            },            actions: {
                edit: "Editar evento",
                activate: "Activar", 
                deactivate: "Desactivar",
                delete: "Eliminar evento",
                cancel: "❌ Cancelar",
                create: "➕ Crear", 
                update: "💾 Actualizar",
                saving: "⏳ Guardando...",
                retry: "🔄 Reintentar"
            },            messages: {
                nameRequired: "¡El nombre del evento es obligatorio!",
                datesRequired: "¡Las fechas de inicio y fin son obligatorias!",
                createSuccess: "✅ ¡Evento creado con éxito!",
                updateSuccess: "✅ ¡Evento actualizado con éxito !",
                deleteSuccess: "✅ ¡Evento eliminado con éxito !",
                toggleSuccess: "✅ ¡Evento {status}!",
                saveError: "❌ ¡Error al guardar!",
                deleteError: "❌ ¡Error al eliminar!",
                toggleError: "❌ ¡Error al cambiar el estado!",
                loadingError: "Error de carga",
                loadingErrorTitle: "❌ Error de carga",
                confirmDelete: "⚠️ ¿Estás seguro de que quieres eliminar este evento?"
            },
            dates: {
                from: "Desde:",
                to: "Hasta:"
            },
            multipliers: {
                x1: "x1 (Normal)",
                x1_5: "x1.5",
                x2: "x2 (Doble)",
                x3: "x3 (Triple)",
                x5: "x5",
                x10: "x10"
            }        },
        reservation: {
            subtitle: "Gestión de reservas de tu servidor",
            configTitle: "Configuración del bot",
            configSubtitle: "Define el canal de Discord donde /pos estará activo y la temporada correspondiente.",
            channelLabel: "Canal de reservas",
            channelHint: "Los jugadores usarán /pos en este canal",
            selectChannel: "-- Seleccionar un canal --",
            activeSaisonLabel: "Temporada activa",
            activeSaisonHint: "La clasificación de esta temporada se usa para /pos",
            selectActiveSaison: "-- Seleccionar una temporada --",
            tableTitle: "Reservas",
            addRow: "Añadir una fila",
            colSaison: "Temporada",
            colJoueur: "Jugador",
            colZone: "Zona",
            selectSaison: "-- Elegir una temporada --",
            selectJoueur: "-- Elegir un jugador --",
            selectZone: "-- Zona --",
            selectSaisonFirst: "Selecciona primero una temporada",
            noPlayers: "Sin jugadores en esta temporada",
            autoAssign: "Auto-asignar zonas",
            noPreference: "Sin preferencia",
            sessionTitle: "Ficha de reservas",
            sessionSubtitle: "Selecciona o crea una ficha para organizar las reservas.",
            selectSessionLabel: "Ficha activa",
            selectSession: "-- Seleccionar una ficha --",
            newSession: "Nueva ficha",
            newSessionPlaceholder: "Ej: GDC Semana 1",
            createSession: "Crear ficha",
            saveRows: "Guardar ficha",
            comingSoon: "Próximamente",
            comingSoonDescription: "La gestión de reservas estará disponible próximamente. ¡Permanece atento!"
        },
        adminStats: {
            title: "Estadísticas de jugadores",
            subtitle: "Ataques y defensas únicos por jugador",
            season: "Temporada",
            allSeasons: "Todas las temporadas",
            searchPlaceholder: "Buscar un jugador...",
            colName: "Nombre",
            colAttacks: "Ataques únicos",
            colDefenses: "Defensas únicas",
            noPlayers: "No se encontraron jugadores",
            playersCount: "jugador(es) mostrado(s)",
            showZeroLabel: "Jugadores inactivos",
            showZeroPlayers: "Mostrar jugadores con 0 puntos"
        },
        bareme: {
            title: "Configuración de Puntuación",
            subtitle: "Configuración de puntos de ataque y defensa",
            existingBaremes: "Sistemas de Puntuación Existentes",
            newBareme: "Nuevo",
            createBareme: "Crear nuevo sistema de puntuación",
            editBareme: "Editar sistema de puntuación",
            baremeName: "Nombre del sistema de puntuación",
            baremeNamePlaceholder: "Ej: Puntuación GDC Verano 2024",
            type: "Tipo",
            attack: "Ataque",
            defense: "Defensa",
            search: "Buscar...",
            allTypes: "Todos los tipos",
            noBaremes: "No hay sistemas de puntuación creados",
            noResults: "Sin resultados",
            completion: "Completado",
            progression: "Progreso",
            totalBaremes: "Sistemas de Puntuación Total",
            unsavedChanges: "Cambios no guardados",
            reset: "Cancelar",
            save: "Guardar",
            create: "Crear",
            saving: "Guardando...",
            delete: "Eliminar",
            enemy: "enemigo",
            enemies: "enemigos",
            allies: "aliadas",
            ally: "aliado",
            loadingBaremes: "Cargando sistemas de puntuación...",
            nameRequired: "¡Por favor ingrese un nombre para el sistema de puntuación!",
            baremeUpdated: "¡Sistema de puntuación actualizado con éxito!",
            baremeCreated: "¡Sistema de puntuación creado con éxito!",
            baremeDeleted: "¡Sistema de puntuación eliminado con éxito!",
            errorSaving: "¡Error al guardar!",
            errorDeleting: "¡Error al eliminar!",
            confirmDelete: "¿Realmente quiere eliminar el sistema de puntuación \"{name}\"?\n\n¡Esta acción es irreversible!",
            confirmUnsaved: "Tienes cambios no guardados. ¿Descartarlos?",
            addRule: "Agregar regla",
            noRules: "No hay reglas configuradas",
            rank: "Rango",
            points: "Puntos",
            description: "Descripción",
            success: "¡Puntuación guardada con éxito!",
            error: "Error al guardar la puntuación"
        },
        tempo: {
            title: "Configuración Puntos Tempo",
            subtitle: "Recompensa combates defensivos que hacen perder tiempo al oponente",
            existingConfigs: "Configuraciones Existentes",
            newConfig: "Nueva",
            createConfig: "Crear nueva configuración",
            editConfig: "Editar configuración",
            configName: "Nombre de la configuración",
            configNamePlaceholder: "Ej: Puntos Tempo Defensa 2024",
            channels: "Canales asociados",
            selectChannels: "Seleccionar canales",
            timeTiers: "Rangos de tiempo",
            tierConfig: "Configuración de rangos",
            timeRange: "Duración del combate",
            points: "Puntos otorgados",
            min0to5: "0-5 minutos",
            min5to10: "5-10 minutos",
            min10to15: "10-15 minutos",
            min15to20: "15-20 minutos", 
            min20plus: "20+ minutos",
            timeInterval: "Intervalo de tiempo",
            timeRanges: "Rangos de Tiempo y Puntos",
            addTier: "Agregar rango",
            removeTier: "Eliminar este rango",
            addFirstTier: "Agregar primer rango",
            noTiers: "No hay rangos de tiempo definidos",
            minTime: "Mín (min)",
            maxTime: "Máx (min)",
            maxTimeHint: "Dejar vacío para \"ilimitado\"",
            channel: "Canal de Discord",
            selectChannel: "Seleccionar un canal",
            general: "Información General",
            reset: "Restablecer",
            confirmUnsaved: "⚠️ Tienes cambios no guardados. ¿Descartarlos?",
            createNew: "Nueva Configuración Tempo",
            createFirst: "Crear la primera",
            loading: "Cargando configuraciones tempo...",
            saveError: "❌ ¡Error al guardar!",
            deleteError: "❌ ¡Error al eliminar!",
            noConfigs: "No hay configuraciones tempo creadas",
            noChannelsSelected: "No hay canales seleccionados",
            save: "Guardar",
            saving: "Guardando...",
            create: "Crear",
            delete: "Eliminar",
            edit: "Editar",
            nameRequired: "El nombre de la configuración es obligatorio",
            configCreated: "¡Configuración tempo creada con éxito!",
            configUpdated: "¡Configuración tempo actualizada con éxito!",
            configDeleted: "¡Configuración tempo eliminada con éxito!",
            errorSaving: "¡Error al guardar!",
            errorDeleting: "¡Error al eliminar!",
            confirmDelete: "¿Realmente quiere eliminar la configuración \"{name}\"?\n\n¡Esta acción es irreversible!",
            loadingConfigs: "Cargando configuraciones...",
            calculation: "Cálculo de puntos",
            combatDuration: "Duración del combate",
            pointsAwarded: "Puntos otorgados",
            noConfigsAvailable: "No hay configuraciones disponibles"
        },
        player: {
            title: "Jugador",
            profile: "Perfil",
            statistics: "Estadísticas",
            history: "Historial",
            totalPoints: "Puntos totales",
            currentRank: "Rango actual",
            participations: "Participaciones",
            victories: "Victorias",
            winRate: "Tasa de victorias",
            averagePoints: "Puntos promedio",
            bestRank: "Mejor rango",
            lastActivity: "Última actividad",
            points: "puntos",
            attackDetails: "Detalles de ataques",
            defenseDetails: "Detalles de defensas",
            noAttacks: "No hay ataques registrados.",
            noDefenses: "No hay defensas registradas.",            errorLoadingData: "Error: No se pueden recuperar los datos del jugador."
        },
        botNotPresent: {
            title: "Bot no presente en {serverName}",
            subtitle: "El bot de gestión no está instalado en este servidor de Discord.",
            whyTitle: "¿Por qué esta página?",
            reasons: {
                notInstalled: {
                    title: "Bot no instalado",
                    description: "El bot no ha sido añadido a este servidor de Discord"
                },
                insufficientPermissions: {
                    title: "Permisos insuficientes", 
                    description: "El bot no tiene los permisos necesarios en este servidor"
                },
                botRemoved: {
                    title: "Bot eliminado",
                    description: "El bot fue eliminado del servidor después de tu última conexión"
                }
            },
            whatToDoTitle: "¿Qué hacer?",
            actions: {
                inviteBot: {
                    title: "Añadir el bot",
                    description: "Invita el bot a tu servidor de Discord con los permisos apropiados",
                    button: "Invitar bot"
                },                contactAdmin: {
                    title: "Contactar un administrador",
                    description: "Pide ayuda a un administrador.",
                    button: "Abrir Discord"
                },
                checkPermissions: {
                    title: "Verificar permisos",
                    description: "Asegúrate de que el bot tenga los permisos necesarios",
                    button: "Ver permisos"
                }            },
            tryAgain: "Intentar de nuevo",
            contactSupport: "Soporte",
            helpTitle: "¿Necesitas ayuda?",
            helpDescription: "Si encuentras dificultades, consulta la documentación o contacta el soporte.",
            documentation: "Documentación"
        },
        server: {
            title: "Configuración del Servidor",
            subtitle: "Asociación de canales de Discord con tipos de ataque y defensa",
            serverName: "Nombre del servidor",
            serverIcon: "Icono del servidor",
            settings: "Configuración general",
            permissions: "Permisos",
            roles: "Roles",
            channels: "Canales",
            generalConfig: "Configuración General",
            configName: "Nombre de configuración",
            configNamePlaceholder: "Ej: Configuración GDC Verano 2024",
            specialRole: "Rol de gestores",
            noSpecialRole: "Sin rol especial",
            channelsAssociation: "Asociación de Canales",
            attackConfig: "Configuración de Ataque",
            defenseConfig: "Configuración de Defensa",
            tempoConfig: "Configuración de Tempo",
            against: "Contra",
            selectChannel: "Seleccionar un canal",
            instructions: "Instrucciones",
            attackInstruction: "Seleccione canales donde los miembros publicarán sus ataques según el número de enemigos derrotados.",
            defenseInstruction: "Seleccione canales donde los miembros publicarán sus defensas según el número de enemigos repelidos.",
            tempoInstruction: "Seleccione el canal donde los miembros publicarán sus combates defensivos para puntos tempo.",
            specialRoleInstruction: "Opcional - Rol que tendrá permisos especiales en el sistema.",
            discordChannels: "Canales de Discord",
            configuredChannels: "Canales Configurados",
            availableRoles: "Roles Disponibles",
            configuration: "Configuración",
            progression: "Progreso",
            reset: "Reiniciar",
            save: "Guardar configuración",
            saving: "Guardando...",
            unsavedChanges: "Tienes cambios no guardados",
            loadingConfig: "Cargando configuración...",
            configNameRequired: "¡Por favor ingrese un nombre de configuración!",
            configSaved: "¡Configuración guardada con éxito!",
            configSaveFailed: "¡Error al guardar!",
            success: "¡Configuración guardada con éxito!",
            error: "Error al guardar la configuración",
            confirmReset: "¿Realmente quiere reiniciar la configuración?"
        },
        errors: {
            generic: "Ocurrió un error",
            network: "Error de conexión de red",
            unauthorized: "Acceso no autorizado",
            forbidden: "Acceso prohibido",
            notFound: "Recurso no encontrado",
            serverError: "Error interno del servidor",
            timeout: "Tiempo de espera agotado",
            invalidData: "Datos inválidos",
            sessionExpired: "Sesión expirada, por favor inicia sesión nuevamente"
        },
        success: {
            saved: "Guardado con éxito",
            created: "Creado con éxito",
            updated: "Actualizado con éxito",
            deleted: "Eliminado con éxito"
        }
    }
};

// Per-page dictionaries (the French text stays inline as the t() fallback)
const deepMerge = (target, source) => {
    for (const [key, value] of Object.entries(source)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            target[key] = deepMerge(target[key] && typeof target[key] === 'object' ? target[key] : {}, value);
        } else {
            target[key] = value;
        }
    }
    return target;
};
deepMerge(translations.en, enPages);
deepMerge(translations.es, esPages);

const LOCALES = { fr: 'fr-FR', en: 'en-US', es: 'es-ES' };
let currentLocale = 'fr-FR';
// Locale of the selected language, for date/number formatting in helpers outside components
export const getLocale = () => currentLocale;

const translate = (language, key, fallback) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
        if (value && value[k] !== undefined) {
            value = value[k];
        } else {
            // Fallback to French if key not found in current language
            let fallbackValue = translations.fr;
            for (const k of keys) {
                if (fallbackValue && fallbackValue[k] !== undefined) {
                    fallbackValue = fallbackValue[k];
                } else {
                    return fallback; // Return fallback if not found anywhere
                }
            }
            return typeof fallbackValue === 'string' ? fallbackValue : fallback;
        }
    }
    
    return typeof value === 'string' ? value : fallback;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        // Try to get language from localStorage first
        const savedLanguage = localStorage.getItem('preferredLanguage');
        if (savedLanguage && ['fr', 'en', 'es'].includes(savedLanguage)) {
            return savedLanguage;
        }
        
        // Otherwise detect from browser
        const browserLanguage = navigator.language.split('-')[0];
        return ['fr', 'en', 'es'].includes(browserLanguage) ? browserLanguage : 'fr';
    });

    useEffect(() => {
        localStorage.setItem('preferredLanguage', language);
    }, [language]);

    currentLocale = LOCALES[language] || 'fr-FR';

    // t(key, fallback, vars): vars fills the {name} placeholders of the text
    const t = useCallback((key, fallback = key, vars) => {
        const text = translate(language, key, fallback);
        if (!vars || typeof text !== 'string') return text;
        return text.replace(/\{(\w+)\}/g, (match, name) => (vars[name] !== undefined ? vars[name] : match));
    }, [language]);

    const changeLanguage = (newLanguage) => {
        if (['fr', 'en', 'es'].includes(newLanguage)) {
            setLanguage(newLanguage);
        }
    };

    const getCurrentLanguage = () => language;

    const getAvailableLanguages = () => [
        { code: 'fr', name: 'Français', flag: '🇫🇷' },
        { code: 'en', name: 'English', flag: '🇺🇸' },
        { code: 'es', name: 'Español', flag: '🇪🇸' }
    ];

    return (
        <LanguageContext.Provider value={{
            language,
            locale: currentLocale,
            t,
            changeLanguage,
            getCurrentLanguage,
            getAvailableLanguages
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;